import { useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from 'react-query'
import BeatLoader from 'react-spinners/BeatLoader'

import BellLink from 'components/UI/BellLink/BellLink'
import { DiscoverPostsFeed } from 'components-ref/templates'
import { usePageTitle } from 'context/pageTitleContext'
import Footer from 'features/Footer/Footer'
import Header from 'features/Header/Header'
import WithoutHeaderSection from 'layouts/WithoutHeaderSection/WithoutHeaderSection'
import { ProfileHolder } from 'pages/profile/features/ProfileHolder'
import { getPerformerByUsername } from 'services/endpoints/profile'
import { useUserProfile } from 'store'
import { useProfileCacheStore } from 'store/profileCacheStore'
import { type StoryPost } from 'types/api/story'
import { type IProfile } from 'types/interfaces/IProfile'

type Props = {
  username?: string | null
  enabled: boolean
  previewUser?: any
  onBack?: () => void
  showFeed?: boolean
}

type DiscoverProfilePanelProfile = {
  id: string
  username: string
  display_name: string
  role: string
  active_stream: boolean
  stream_url?: string
  online_status: boolean
  last_logged_at: string

  avatar?: any
  cropped_avatar?: any
  cover?: any
  cropped_cover?: any

  follower_count: number
  friend_count: number
  photo_count: number
  video_count: number
  premium_video_count: number
  post_like_count: number

  friends: any
  friendship_status?: any

  isSubscribed: boolean
  subscription_detail?: any[]
  media_categories?: any[]

  show_stories: boolean
  friend_button: boolean
  follow_count: boolean
  public_friends: boolean
  like_count: boolean
  show_mini_bio: boolean
  show_online_status: boolean

  mini_bio?: string
  subscription_price: number
  status: string
  overall_revenue?: number
  rebill?: boolean
}

const buildPreviewProfile = (
  previewUser: any
): DiscoverProfilePanelProfile | undefined => {
  const id =
    previewUser?.id ??
    previewUser?.user_id

  if (!id || !previewUser?.username) {
    return undefined
  }

  return {
    id,
    username: previewUser.username,
    display_name: previewUser.display_name ?? '',

    avatar: previewUser.avatar,
    cropped_avatar: previewUser.cropped_avatar,
    cover: previewUser.cover,
    cropped_cover: previewUser.cropped_cover,

    role: previewUser.role ?? 'model',
    rebill: previewUser.rebill,

    active_stream:
      previewUser.active_stream ?? false,

    stream_url:
      previewUser.stream_url,

    online_status:
      previewUser.online_status ?? false,

    last_logged_at:
      previewUser.last_logged_at ?? '',

    follower_count:
      previewUser.follower_count ?? 0,

    friend_count:
      previewUser.friend_count ?? 0,

    photo_count:
      previewUser.photo_count ?? 0,

    video_count:
      previewUser.video_count ?? 0,

    premium_video_count:
      previewUser.premium_video_count ?? 0,

    post_like_count:
      previewUser.post_like_count ?? 0,

    friends:
      previewUser.friends ??
      previewUser.friends_status ??
      previewUser.friendsStatus ??
      'Not friend',

    friendship_status:
      previewUser.friendship_status ?? {},

    isSubscribed:
      previewUser.isSubscribed ?? false,

    subscription_detail:
      previewUser.subscription_detail ?? [],

    media_categories:
      previewUser.media_categories ?? [],

    show_stories:
      previewUser.show_stories ?? false,

    friend_button:
      previewUser.friend_button ?? true,

    follow_count:
      previewUser.follow_count ?? true,

    public_friends:
      previewUser.public_friends ?? true,

    like_count:
      previewUser.like_count ?? true,

    show_mini_bio:
      previewUser.show_mini_bio ?? true,

    show_online_status:
      previewUser.show_online_status ?? true,

    mini_bio:
      previewUser.mini_bio ?? '',

    subscription_price:
      previewUser.subscription_detail?.[0]
        ?.subscription_plan?.price ??
      Number(
        previewUser.subscription_detail?.[0]
          ?.price ?? 0
      ),

    status:
      previewUser.status ?? '',

    overall_revenue:
      previewUser.overall_revenue,
  }
}

const buildPanelProfile = (
  profile: IProfile | undefined
): DiscoverProfilePanelProfile | undefined => {
  if (!profile?.id || !profile?.username) return undefined

  return {
    id: profile.id,
    username: profile.username,
    display_name: profile.display_name ?? '',
    avatar: profile.avatar,
    cropped_avatar: profile.cropped_avatar,
    cover: profile.cover,
    cropped_cover: profile.cropped_cover,

    role: profile.role ?? 'model',
    active_stream: profile.active_stream ?? false,
    stream_url: profile.stream_url,
    online_status: profile.online_status ?? false,
    last_logged_at: profile.last_logged_at ?? '',

    follower_count: profile.follower_count ?? 0,
    friend_count: profile.friend_count ?? 0,
    photo_count: profile.photo_count ?? 0,
    video_count: profile.video_count ?? 0,
    premium_video_count: profile.premium_video_count ?? 0,
    post_like_count: profile.post_like_count ?? 0,

    friends: profile.friends_status ?? 'Not friend',
    friendship_status: profile.friendship_status ?? {},

    isSubscribed: profile.isSubscribed ?? false,
    subscription_detail: profile.subscription_detail ?? [],
    media_categories: profile.media_categories ?? [],

    show_stories: profile.show_stories ?? false,
    friend_button: profile.friend_button ?? true,
    follow_count: profile.follow_count ?? true,
    public_friends: profile.public_friends ?? true,
    like_count: profile.like_count ?? true,
    show_mini_bio: profile.show_mini_bio ?? true,
    show_online_status: profile.show_online_status ?? true,

    mini_bio: profile.mini_bio ?? '',
    subscription_price:
      profile.subscription_detail?.[0]?.subscription_plan?.price ??
      Number(profile.subscription_detail?.[0]?.price ?? 0),
    status: profile.status ?? '',
    overall_revenue: profile.overall_revenue,
    rebill: (profile as any).rebill,
  }
}

export const DiscoverProfilePanel = ({
  username,
  enabled,
  previewUser,
  onBack,
  showFeed = true,
}: Props) => {
  const queryClient = useQueryClient()
  const { setTitle } = usePageTitle()
  const { t } = useTranslation()

  const userData = useUserProfile()
  const viewerId = userData?.id ?? null

  const scrollRef = useRef<HTMLDivElement>(null)

  const [stories, setStories] = useState<StoryPost[]>([])
  const [subscribeModalOpen, setSubscribeModalOpen] = useState(false)

  const getProfileFromCache = useProfileCacheStore((s) => s.getProfile)
  const setProfileInCache = useProfileCacheStore((s) => s.setProfile)

  const cachedProfile: IProfile | undefined =
    username ? getProfileFromCache(username, viewerId) : undefined

  const isViewingOwnProfile =
    !!userData?.username &&
    !!username &&
    userData.username.toLowerCase() === username.toLowerCase()

  const previewProfile = useMemo(
    () => buildPreviewProfile(previewUser),
    [previewUser]
  )

  const profileKey = ['discover-profile', username, viewerId]

  const { data, isLoading, isFetching } = useQuery<IProfile | undefined>(
    profileKey,
    async () => {
      if (!username) return undefined
      return await getPerformerByUsername(username)
    },
    {
      enabled: enabled && !!username && !isViewingOwnProfile,
      initialData: undefined,
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      staleTime: 20_000,
      onSuccess: (fresh) => {
        if (fresh && username) {
          setProfileInCache(username, fresh, viewerId)
        }
      },
    }
  )

  useEffect(() => {
    if (enabled) {
      setTitle(t('profile'))
    } else {
      setTitle(t('reels'))
    }
  }, [enabled, setTitle, t])

  const profileData: IProfile | undefined = useMemo(() => {
    if (isViewingOwnProfile) {
      return userData ? (userData as unknown as IProfile) : undefined
    }

    const dataMatchesUsername =
      !!data?.username &&
      !!username &&
      data.username.toLowerCase() === username.toLowerCase()

    const cacheMatchesUsername =
      !!cachedProfile?.username &&
      !!username &&
      cachedProfile.username.toLowerCase() === username.toLowerCase()

    if (dataMatchesUsername && data) return data
    if (cacheMatchesUsername && cachedProfile) return cachedProfile

    return undefined
  }, [
    isViewingOwnProfile,
    userData,
    cachedProfile,
    data,
    username,
  ])

  const panelProfile = useMemo(() => {
    return buildPanelProfile(profileData) ?? previewProfile
  }, [profileData, previewProfile])

  const refetchProfile = async () => {
    await queryClient.invalidateQueries(profileKey)
  }

  if ((isLoading || isFetching) && !panelProfile) {
    return (
      <div className="discover-profile-panel__loader">
        <BeatLoader size={12} color="#999" />
      </div>
    )
  }

  if (!panelProfile) return null

  return (
    <WithoutHeaderSection customClass="profile-woh discover-profile-woh has-real-cover">
      <div className="discover-profile-panel__mobile-header">
        <Header title="Profile" rightElement={<BellLink />} linkBack={onBack} />
      </div>
      <div ref={scrollRef} className="discover-profile-panel">

        <ProfileHolder
          key={`profile-holder-${panelProfile.id}`}
          fromDiscoverProfilePanel={true}
          discoverShowFeed={showFeed}
          scrollContainerRef={scrollRef}
          stories={stories}
          onStoryDelete={(storyId: string) => {
            setStories((prev) =>
              prev.filter((story) => story.id !== storyId)
            )
          }}
          id={panelProfile.id}
          role={panelProfile.role}
          active_stream={panelProfile.active_stream}
          stream_url={panelProfile.stream_url}
          online={panelProfile.online_status}
          last_logged_at={panelProfile.last_logged_at}
          cover={
            panelProfile.cover?.url ??
            panelProfile.cropped_cover?.url ??
            null
          }
          avatar={
            userData?.id === panelProfile.id
              ? userData?.avatar?.url
              : panelProfile.cropped_avatar?.url ||
                panelProfile.avatar?.url ||
                null
          }
          display_name={panelProfile.display_name}
          expiry_at={panelProfile.subscription_detail?.[0]?.expiry_at}
          follower_count={panelProfile.follower_count}
          friend_count={panelProfile.friend_count}
          username={panelProfile.username}
          show_stories={panelProfile.show_stories}
          friend_button={panelProfile.friend_button}
          isSubscribed={panelProfile.isSubscribed}
          refetchProfileData={refetchProfile}
          photo_count={panelProfile.photo_count}
          premium_video_count={panelProfile.premium_video_count}
          video_count={panelProfile.video_count}
          friends_status={panelProfile.friends}
          friendships={panelProfile.friendship_status || {}}
          mini_bio={panelProfile.mini_bio}
          subscribeModalOpen={subscribeModalOpen}
          setSubscribeModalOpen={setSubscribeModalOpen}
          show_follower_count={panelProfile.follow_count}
          public_friends={panelProfile.public_friends}
          show_likes_count={panelProfile.like_count}
          show_mini_bio={panelProfile.show_mini_bio}
          post_like_count={panelProfile.post_like_count}
          show_online_status={panelProfile.show_online_status}
          status={panelProfile.status}
          overall_revenue={panelProfile.overall_revenue}
          tab={undefined}
          rebill={
            panelProfile.subscription_detail?.[0]?.rebill ??
            panelProfile.rebill
          }
          updated_at={panelProfile.subscription_detail?.[0]?.updated_at}
          ownProfile={isViewingOwnProfile}
        />

      <DiscoverPostsFeed
        feedNav={['all', 'videos', 'premium', 'reels']}
        id={panelProfile.id}
        showFeed={showFeed}
        user={panelProfile}
        enabled={enabled && !!profileData}
        username={panelProfile.username}
        setSubscribeModalOpen={setSubscribeModalOpen}
        profileLoading={!profileData}
      />

        <Footer />
      </div>
    </WithoutHeaderSection>
  )
}